import {
  getUserDefaultMetricForProject,
  hasMetricModeForProject,
} from '@resistapp/common/features';
import { MetricMode, User } from '@resistapp/common/types';
import { chain } from 'lodash';

export type ProjectPage = 'index' | 'risk' | 'reduction' | 'research';

const pageByMetricMode: Record<MetricMode, Exclude<ProjectPage, 'research'>> = {
  [MetricMode.ARGI]: 'index',
  [MetricMode.RISK]: 'risk',
  [MetricMode.REDUCTION]: 'reduction',
};

const metricModeByPage: Record<Exclude<ProjectPage, 'research'>, MetricMode> = {
  index: MetricMode.ARGI,
  risk: MetricMode.RISK,
  reduction: MetricMode.REDUCTION,
};

export function isOverviewProjectPage(page: string): page is Exclude<ProjectPage, 'research'> {
  return page === 'index' || page === 'risk' || page === 'reduction';
}

export function getBestPageForProject(user: User | undefined | null, projectId?: number | null): ProjectPage {
  if (projectId == null) {
    return 'research';
  }

  const preferredMetric = getUserDefaultMetricForProject(user, projectId);
  const orderedMetrics = chain([preferredMetric, ...Object.values(MetricMode)])
    .compact()
    .uniq()
    .value();
  const bestMetric = orderedMetrics.find(metricMode => hasMetricModeForProject(user, metricMode, projectId));

  return bestMetric ? pageByMetricMode[bestMetric] : 'research';
}

export function canStayOnProjectPage(
  user: User | undefined | null,
  currentPage: string,
  projectId?: number | null,
): boolean {
  return isOverviewProjectPage(currentPage) && projectId != null
    ? hasMetricModeForProject(user, metricModeByPage[currentPage], projectId)
    : currentPage === 'research';
}

export function getProjectNavigationPage(params: {
  user: User | undefined | null;
  currentPage: string;
  projectId?: number | null;
}): ProjectPage {
  const { currentPage, projectId, user } = params;

  if (currentPage === 'research' || currentPage === 'admin') {
    return 'research';
  }

  if (isOverviewProjectPage(currentPage) && canStayOnProjectPage(user, currentPage, projectId)) {
    return currentPage;
  }

  return getBestPageForProject(user, projectId);
}
